import React, { useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import * as yup from 'yup';
import config from 'config';
import { 
  Box, 
  Button, 
  Checkbox, 
  CircularProgress, 
  FormControlLabel, 
  Link, 
  TextField, 
  Typography 
} from '@mui/material';
import useRouter from '../../../utils/useRouter';
import { borderRadius, palette, spaces } from '../../../assets/styles/themes/tokens';
import isFakeEmail from '../../../validators/isFakeEmail';
import hasValidLibraryDomain from '../../../validators/hasValidLibraryDomain';
import canUserCreateSubscription from '../../../validators/canUserCreateSubscription';
import { Controller, useForm } from 'react-hook-form';
import EmptyCheckbox from '../../../assets/lernsets/icons/checkbox.svg';
import SelectedCheckbox from '../../../assets/lernsets/icons/selectedCheckbox.svg';
import { 
  AmpliSubscriptionSubmitted, 
  externalLinks, 
  routes 
} from '../../../constants';
import useCaptcha from '../../../utils/useCaptcha';
import { DiscountInformation, LicenceModel } from '@klab-berlin/api-sdk/lib/types/responses/Billing';
import { useSelector } from 'react-redux';
import { getSubscription, getSubscriptionUpgradePreview, selectUser } from '../../../reducers/user.selectors';
import { ZuoraPaymentMethod } from '@klab-berlin/api-sdk/lib/services/billing';
import { PaymentData, ZuoraPaymentPageResponse } from '../../../types/ZuoraPayments';
import { currencyFormat } from '../../../utils/format';
import { PaymentPeriod } from '@klab-berlin/api-sdk/lib/types/common';
import { 
  fetchMe, 
  upgradeToDocuments, 
  upgradeToKombi, 
  getSubscription as subscriptionAction,
} from '../../../actions/user.actions';
import useAction from '../../../utils/useAction';
import { getAmpliSubscriptionSubmittedPayload } from '../../../utils/ampli';
import services from '../../../services';
import { RegisterLibraryPayload, RegisterPremiumPayload } from '@klab-berlin/api-sdk/lib/types/requests/Registration';
import { addError } from '../../../actions/errors.actions';
import { notifyBugsnagHandledError } from '../../../utils/bugsnagClient';
import { trackingEvents } from '../../../services/tracking/trackConfig';
import { UpgradePayload } from '@klab-berlin/api-sdk/lib/types/requests/User';
import { getPaymentMethodData } from '../../../utils/payment';
import PaymentMethods from '../../../components/SinglePagePaymentMethods/PaymentMethods';
import CheckIcon from '@mui/icons-material/Check';
import SuccessModal from '../../../components/SuccessModal';
import { AmplitudeTrackPayload } from '@klab-berlin/api-sdk/lib/types/requests/Track';
import { UserResponseData } from '@klab-berlin/api-sdk/lib/types/responses/User';
import { PaymentMode } from '../PaymentPage/PaymentPage';
import { addDays, format } from 'date-fns';
import { dateFormat } from '../../../constants';

interface FormFields {
  licenceAgreement: boolean;
  showPaymentForm: boolean;
  email?: string;
  isInterestedInReceivingRecommendations?: boolean;
}

interface PaymentFormProps {
  licence: LicenceModel;
  paymentMode: PaymentMode;
  paymentPeriod: PaymentPeriod;
  redirect?: string;
  library?: string;
  promoCode?: string;
  promoCodeDiscountInfo?: DiscountInformation;
}

interface LicenceInformation {
  endDate: string;
  price: string;
  totalPrice: string;
  subtitle?: string;
  title?: string;
  period: string;
  months: string;
}

const getLicenceInformation = (licenceData: LicenceModel): LicenceInformation => {
  return {
    endDate: format(addDays(Date.now(), 14), dateFormat),
    price: currencyFormat(licenceData.price),
    totalPrice: currencyFormat(licenceData.totalPrice),
    subtitle: licenceData.landingPageSubtitle,
    title: licenceData.landingPageTitle,
    period: licenceData.period,
    months: licenceData.period
  };
};

export interface RegisterPremiumTrackingParams {
  registeredUser: { _id?: string } | null | undefined;
  licence: LicenceModel | null | undefined;
  promoCodeDiscountInfo?: DiscountInformation;
  payload: { payment?: { licenceModelId?: number; paymentPeriod?: PaymentPeriod; promoCode?: string | null } };
  paymentData: { type?: string };
}

export function buildRegisterPremiumTrackingPayload(params: RegisterPremiumTrackingParams) {
  const { registeredUser, licence, promoCodeDiscountInfo, payload, paymentData } = params;
  const hasLicence = licence != null;
  const priceFromLicence = hasLicence ? licence.price : undefined;
  const hasValidLicencePrice = typeof priceFromLicence === 'number' && Number.isFinite(priceFromLicence);
  const hasValidPromoPrice = typeof promoCodeDiscountInfo?.newPricePerMonth === 'number' &&
    Number.isFinite(promoCodeDiscountInfo.newPricePerMonth);

  const rawMonthlyPrice = hasValidPromoPrice
    ? promoCodeDiscountInfo!.newPricePerMonth
    : (hasValidLicencePrice ? priceFromLicence : 0);
  const monthlyPrice = typeof rawMonthlyPrice === 'number' && Number.isFinite(rawMonthlyPrice) ? rawMonthlyPrice : 0;
  const orderValue = Math.round(monthlyPrice * 12 * 100) / 100;
  const currencyCode = 'EUR';

  const promoCode = payload?.payment?.promoCode;
  const discountCode = promoCode != null && promoCode !== '' ? String(promoCode) : undefined;

  return {
    other: {
      user_id: registeredUser?._id ?? undefined,
      order_value: Number.isFinite(orderValue) ? orderValue : 0,
      currency: currencyCode,
      discount_code: discountCode,
      subscription: {
        licenceModelId: payload?.payment?.licenceModelId ?? undefined,
        paymentMethod: paymentData?.type ?? undefined,
        paymentPeriod: payload?.payment?.paymentPeriod ?? undefined
      }
    }
  };
}

const PaymentForm: React.FC<PaymentFormProps> = (props) => {
  const { t } = useTranslation();
  const { router } = useRouter();
  const fetchMeAction = useAction(fetchMe);
  const getSubscriptionAction = useAction(subscriptionAction);
  
  const {
    licence, 
    paymentMode,
    library,
    promoCodeDiscountInfo,
    paymentPeriod,
    redirect,
    promoCode,
  } = props;

  const {
    licenceModelId: licenceId,
    isTrial: isTrialLicence,
  } = licence;

  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<ZuoraPaymentMethod>();
  const [payPalPaymentData, setPayPalPaymentData] = useState<PaymentData | null>(null);
  const [isSubmitDisabled, setIsSubmitDisabled] = useState<boolean>(false);
  const [isPaymentConfirmed, setIsPaymentConfirmed] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showPaymentForm, setShowPaymentForm] = useState<boolean>(false);
  const [isError, setIsError] = useState<boolean>(false);
  const [isGettingUserInfo, setIsGettingUserInfo] = useState<boolean>(false);
  const user = useSelector(selectUser);
  const subscription = useSelector(getSubscription);
  const licenceData = getLicenceInformation(licence);
  const subscriptionUpgradePreview = useSelector(getSubscriptionUpgradePreview);
  const [renderCaptcha, isCaptchaSolved] = useCaptcha();
  if (promoCodeDiscountInfo) {
    licenceData.price = currencyFormat(promoCodeDiscountInfo?.newPricePerMonth);
  }

  const isLibraryLicence = !!library && paymentMode === PaymentMode.PremiumRegistration;
  const upgradeToDocumentsAction = useAction(upgradeToDocuments);
  const upgradeToKombiAction = useAction(upgradeToKombi);

  // We need to use refs for these values because onPaymentMethodCreated() is a callback function.
  // Callbacks take the initial state value and ignore updates.
  const licenceInfoAreaRef = useRef({ promoCode, paymentPeriod });
  const licenceIdRef = useRef(licenceId);
  const userRef = useRef(user);

  const isPaymentMethodPayPal = (paymentMethod: ZuoraPaymentMethod) => paymentMethod === 'braintree';

  const DEFAULT_DOMAIN_ERROR_KEY = 'You can only register using your university email address.';

  useEffect(() => {
    if (selectedPaymentMethod && isPaymentMethodPayPal(selectedPaymentMethod)) {
      setIsSubmitDisabled(!payPalPaymentData);
    }
  }, [selectedPaymentMethod, payPalPaymentData]);

  useEffect(() => {
    if (promoCodeDiscountInfo?.valid) {
      licenceInfoAreaRef.current = { promoCode, paymentPeriod };
    } else {
      licenceInfoAreaRef.current = { promoCode: undefined, paymentPeriod };
    }
    licenceIdRef.current = licenceId;
  }, [promoCode, paymentPeriod, licenceId, promoCodeDiscountInfo]);
  
  useEffect(() => {
    userRef.current = user;
  }, [user]);

  const onPaymentMethodSelect = (paymentMethod: ZuoraPaymentMethod) => {
    setShowPaymentForm(true);
  
    if (paymentMethod !== selectedPaymentMethod) {
      setSelectedPaymentMethod(paymentMethod);
      setPayPalPaymentData(null);
    }
  
    if (!isPaymentMethodPayPal(paymentMethod)) {
      setIsSubmitDisabled(false);
    }
  };

  const createEmailValidationSchema = () => {
    if (paymentMode !== PaymentMode.PremiumRegistration) {
      return undefined;
    }

    return {
      email: yup
        .string()
        .email('The email is not valid')
        .required('This field is required')
        .test('Fake email', 'The email is not valid', isFakeEmail)
        .test({
          name: 'Bad Domain',
          test: async function (this: yup.TestContext, email: string) {
            if (!library) {
              return true;
            }
            
            try {
              const result = await hasValidLibraryDomain(email, library);
              if (result.valid) {
                return true;
              }
              
              const errorMessage = result.libraryName
                ? t(
                  'You can only register using your university email address ({{libraryName}}).',
                  { libraryName: result.libraryName }
                )
                : t(DEFAULT_DOMAIN_ERROR_KEY);
              
              return this.createError({ message: errorMessage });
            } catch (error) {
              notifyBugsnagHandledError(new Error(DEFAULT_DOMAIN_ERROR_KEY));
              return this.createError({
                message: t(DEFAULT_DOMAIN_ERROR_KEY)
              });
            }
          }
        })
        .test(
          'Existing Email',
          'The email you entered already exists.',
          (email: string) => canUserCreateSubscription(email)
        )
    };
  };

  const formValidator = yup.object<FormFields>({
    licenceAgreement: yup
      .boolean()
      .oneOf([true], 'You have to agree to our terms and conditions'),
    showPaymentForm: yup
      .boolean()
      .oneOf([true], 'To register, you must specify a payment method'),
  }).concat(yup.object(createEmailValidationSchema()));

  const {
    errors,
    handleSubmit,
    register,
    getValues,
    control,
    formState,
    setValue,
  } = useForm<FormFields>({
    mode: 'onBlur',
    validationSchema: formValidator,
  });

  const onSubmit = handleSubmit(async (formData) => {
    setIsSubmitDisabled(true);
    setIsLoading(true);
    setIsError(false);
  
    // For PayPal, payment method is created before the payment form is submitted.
    // So we can already check that the userPaymentData exist and upgrade the user.
    if (selectedPaymentMethod && isPaymentMethodPayPal(selectedPaymentMethod) && payPalPaymentData) {
      switch (paymentMode) {
        case PaymentMode.PremiumUpgrade:
          return upgradeUser(payPalPaymentData);
        case PaymentMode.PremiumRegistration:
          if (!formData.email) {
            setIsLoading(false);
            throw new Error('Can not register a user with no valid email.');
          }
          return registerUser(payPalPaymentData, formData.email, formData.isInterestedInReceivingRecommendations);
      }
    }
  
    if (isLibraryLicence && formData.email) {
      registerLibraryUser(formData.email, formData.isInterestedInReceivingRecommendations);
    } else {
      // Creates Zuora payment method. This then triggers onPaymentMethodCreated function.
      window.Z.submit();
    }
  });

  const sendSubscriptionSubmittedEvent = (user?:UserResponseData) => {
    const billingCycle = 
      subscription?.status === 'Active' && 
      subscription?.paymentPeriod?.type
        ? subscription?.paymentPeriod?.type 
        : licenceInfoAreaRef.current.paymentPeriod;

    const eventProperties: AmplitudeTrackPayload = getAmpliSubscriptionSubmittedPayload({
      licence,
      promoCode,
      billingCycle,
      paymentMethod: selectedPaymentMethod,
      userType: userRef?.current?.type,
      licenceModelId: subscription?.licence?.licenceModelId,
    } as AmpliSubscriptionSubmitted);
    
    if (eventProperties) {
      services.track.ampliServerSideEventTrack(true, eventProperties, user?._id, true);
    }
  };

  const registerLibraryUser = (userEmail: string, isInterestedInReceivingRecommendations?: boolean) => {
    if (library) {
      const payload: RegisterLibraryPayload = {
        email: userEmail,
        library,
        isInterestedInReceivingRecommendations: !!isInterestedInReceivingRecommendations,
      };
      services.registration.registerLibrary(payload)
        .then(sendSubscriptionSubmittedEvent)
        .then(() => {
          setIsPaymentConfirmed(true);
          setIsLoading(false);
        })
        .catch(() => {
          addError('Ups, something went wrong');
          setIsError(true);
          setIsLoading(false);
        });
    }
  };
  
  const sendVWOEvent = () => {
    const VWO = window.VWO || [];
    VWO.event = VWO.event || function () {
      // eslint-disable-next-line prefer-rest-params
      VWO.push(['event'].concat([].slice.call(arguments)));
    };
    VWO.event('signUp');
  };

  const registerUser = 
  (paymentData: PaymentData, userEmail: string, isInterestedInReceivingRecommendations?: boolean) => {
    const paymentMethodId = paymentData.paymentMethodId || paymentData.braintreeToken || '';

    const payload: RegisterPremiumPayload = {
      data: {
        email: userEmail,
        isInterestedInReceivingRecommendations: !!isInterestedInReceivingRecommendations,
      },
      payment: {
        licenceModelId: licenceIdRef.current,
        paymentMethod: getPaymentMethodData(paymentData.type, paymentMethodId),
        paymentPeriod: licenceInfoAreaRef.current.paymentPeriod,
        promoCode: licenceInfoAreaRef.current.promoCode ? licenceInfoAreaRef.current.promoCode : null,
      },
    };
    if (redirect) {
      payload.data.redirectTo = {
        docId: redirect
      };
    }
  
    services.registration.registerPremium(payload)
      .then((registeredUser) => {
        sendSubscriptionSubmittedEvent(registeredUser);
        return registeredUser;
      })
      .then((registeredUser) => {
        const trackingPayload = buildRegisterPremiumTrackingPayload({
          registeredUser,
          licence,
          promoCodeDiscountInfo,
          payload,
          paymentData
        });
        services.track.eventTrack(trackingEvents.registerPremiumFinish, trackingPayload);
        sendVWOEvent();
        setIsPaymentConfirmed(true);
        setIsLoading(false);
      })
      .catch(() => {
        addError('Ups, something went wrong');
        setIsError(true);
        setIsLoading(false);
      });
  };
  
  const upgradeUser = (paymentData: PaymentData) => {
    const paymentMethodId = paymentData.paymentMethodId || paymentData.braintreeToken || '';
  
    if (userRef.current) {
      const paymentMethod = getPaymentMethodData(paymentData.type, paymentMethodId);
  
      const upgradePayload: UpgradePayload = (!user?.isBibliothekUser && !user?.isCourseUser) ? {
        licenceData: { licenceModelId: licenceIdRef.current },
        paymentMethod: paymentMethod,
        paymentPeriod: licenceInfoAreaRef.current.paymentPeriod,
        promoCode: licenceInfoAreaRef.current.promoCode ? licenceInfoAreaRef.current.promoCode : null,
        trackingData: {},
      } : {
        licenceData: { licenceModelId: licenceIdRef.current }
      } as UpgradePayload;
  
      const upgradeAction = user?.isCourseUser || user?.isBibliothekUser ?
        upgradeToKombiAction :
        upgradeToDocumentsAction;
  
      upgradeAction(userRef.current.id, upgradePayload)
        .then(() => sendSubscriptionSubmittedEvent())
        .then(() => {
          services.track.eventTrack(
            trackingEvents.upgradeFinish,
            {
              user,
              other: {
                subscription: {
                  licenceModelId: upgradePayload.licenceData.licenceModelId,
                  paymentMethod,
                  paymentPeriod: upgradePayload.paymentPeriod
                }
              }
            }
          );

          setIsPaymentConfirmed(true);
          setIsLoading(false);
        })
        .catch(() => {
          addError('Ups, something went wrong');
          setIsError(true);
          setIsSubmitDisabled(false);
          setIsLoading(false);
        });
    } else {
      addError('Ups, something went wrong');
      setIsError(true);
      notifyBugsnagHandledError(new Error(`User can't be upgraded because user not found, user: ${user}`));
      setIsSubmitDisabled(false);
      setIsLoading(false);
    }
  };
  
  const onPaymentMethodCreated =
      (paymentMethod: ZuoraPaymentMethod, response: ZuoraPaymentPageResponse) => {
        if (!response.success) {
          addError('Error while setting up payment method, please refresh');
          setIsError(true);
          setIsLoading(false);
          setIsSubmitDisabled(false);
          return;
        }
  
        const paymentData = {
          type: paymentMethod,
          paymentMethodId: response.refId,
          braintreeToken: response.auth,
          licenceId: licenceIdRef.current,
        };
  
        if (isPaymentMethodPayPal(paymentMethod)) {
          setPayPalPaymentData(paymentData);
        } else {
          const email = getValues().email;
          const isInterestedInReceivingRecommendations = getValues().isInterestedInReceivingRecommendations;
          switch (paymentMode) {
            case PaymentMode.PremiumUpgrade:
              upgradeUser(paymentData);
              break;
            case PaymentMode.PremiumRegistration:
              if (!email)
                throw new Error('Can not register a user with no valid email.');
              registerUser(paymentData, email, isInterestedInReceivingRecommendations);
              break;
          }
        }
      };
  
  const onClientSidePaymentErrorHandler = () => {
    setIsSubmitDisabled(false);
    setIsLoading(false);
  };

  const onSuccessModalConfirmButtonClick = async () => {
    try {
      setIsGettingUserInfo(true);
      await fetchMeAction(true);  
      await getSubscriptionAction(); 
      router.push(`/${routes.search.root}`);
    } catch (error) {
      setIsGettingUserInfo(false);
      notifyBugsnagHandledError(error as Error);
    }
    setIsGettingUserInfo(false);
  };

  const onCloseModal = () => {
    if (isPaymentConfirmed) {
      // reset form
      setIsPaymentConfirmed(false);
      setShowPaymentForm(false);
      setSelectedPaymentMethod(undefined);
      setValue('email', '');
      setValue('showPaymentForm', false);
    }
    setIsError(false);
    setIsSubmitDisabled(false);
  };

  const getDialogInfo = () => {
    const dialogTitle = isError ? t('Payment could not be completed') :
      PaymentMode.PremiumRegistration === paymentMode ? t('Just one step') : `${t('Your upgrade was successful')!}`;

    const dialogContent = isError 
      ? t('A technical issue occurred while processing your payment. Please try again.') 
      : PaymentMode.PremiumRegistration === paymentMode 
        ? t('Your registration was successful. Just click on the link in your confirmation email to get started') 
        : t('Thank you for your trust. Your upgrade is complete! You can find the receipt in your email inbox.');

    return {
      dialogTitle,
      dialogContent
    };
  };  

  const getButtonText = () => {
    if (isTrialLicence) {
      return t('Start premium trial');
    } else if (isLibraryLicence) {
      return t('Registration');
    } else {
      return t('Order with costs');
    }
  };

  const renderLicenceAgreementText = () => {
    return (
      <>
        <Box>
          <FormControlLabel
            sx={{ '&.MuiFormControlLabel-root': { marginX: 0 } }}
            control={
              <Checkbox
                data-testid='licence-agreement-checkbox'
                inputRef={register}
                name='licenceAgreement' 
                color='primary' 
                size='small'
                icon={<EmptyCheckbox />}
                checkedIcon={<SelectedCheckbox />}
              />
            }
            labelPlacement='end'
            label={
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Typography variant='caption' color={palette.text.secondary}>
                  <Trans 
                    i18nKey={
                      'I hereby declare that I have acknowledged and ' +
                      'accepted the <0>AGB</0> and the <1>Widerrufsbelehrung</1>.'
                    }
                    components={[
                      <Link 
                        key='0' 
                        href={externalLinks.tac} 
                        target='_blank' 
                        rel='noopener noreferrer'
                      >
                        {t('AGB')}
                      </Link>,
                      <Link 
                        key='1' 
                        href={externalLinks.widerrufsbelehrung} 
                        target='_blank' 
                        rel='noopener noreferrer'>
                        {t('Right of withdrawal')}
                      </Link>
                    ]}
                  />
                </Typography>
              </Box>
            }
          />
          {errors.licenceAgreement && (
            <Box sx={{ paddingLeft: spaces.md }}>
              <Typography variant='caption' color={palette.error.main} data-testid='licence-agreement-error'>
                {t(errors.licenceAgreement.message as string)}
              </Typography>
            </Box>
          )}
        </Box>
        {
          paymentMode === PaymentMode.PremiumRegistration &&
          <FormControlLabel
            sx={{ '&.MuiFormControlLabel-root': { marginX: 0 } }}
            control={
              <Checkbox
                data-testid='recommendation-agreement-checkbox'
                inputRef={register}
                name='isInterestedInReceivingRecommendations'
                sx={{ alignSelf: 'flex-start' }}
                color='primary' 
                size='small'
                icon={<EmptyCheckbox />}
                checkedIcon={<SelectedCheckbox />}
                defaultChecked={true}
              />
            }
            labelPlacement='end'
            label={
              <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: spaces.md,
              }}>
                <Typography variant='caption' color={palette.text.secondary}>
                  {t('Yes, I would like to receive the newsletter with free material recommendations,' + 
                  ' tips for everyday school life, and exclusive invitations to webinars.' +
                  ' (Unsubscribable at any time).')
                  }
                </Typography>
                <Typography variant='caption' color={palette.text.secondary}>
                  <Trans i18nKey='Our current <0>privacy policy</0> applies.'
                    components={[
                      <Link 
                        key='0'
                        href={externalLinks.dataPolicy} 
                        target='_blank' 
                        rel='noopener noreferrer'
                      >
                        {t('privacyPolicy')}
                      </Link>
                    ]}
                  />
                </Typography>
              </Box>
            }
          />
        }
      </>
    );
  };

  const isButtonDisabled = isSubmitDisabled || formState.isSubmitting || !isCaptchaSolved;

  return (
    <Box
      component='form' 
      onSubmit={onSubmit}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: spaces.lg,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: spaces.md,
        }}
      >
        {paymentMode === PaymentMode.PremiumRegistration && (
          <Box
            data-testid='payment-form-email'
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: spaces.xs,
            }}
          >
            <Typography variant='body1' color={palette.text.primary}>{t('E-Mail-Address')}</Typography>
            <TextField 
              data-testid='email-input'
              variant='outlined'
              placeholder={t('eg max.mustermann@beispiel.de')}
              size='small'
              name='email'
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: borderRadius.xs,
                },
              }}
              inputRef={register}
              error={!!errors.email}
              helperText={t(errors.email?.message as string)}
            />
          </Box>
        )}
        {!isLibraryLicence && <Box
          data-testid='payment-form-payment-method'
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: spaces.xs,
          }}
        >
          <Typography variant='body1' color={palette.text.primary}>{t('Payment method')}</Typography>
          {
            (user?.isBibliothekUser || user?.isCourseUser) &&
            subscriptionUpgradePreview &&
            ['bankTransfer', 'creditCard', 'sepa', 'paypal'].includes(subscriptionUpgradePreview.paymentMethod) &&
            <Typography variant='caption' color={palette.text.primary}>
              {t('Payment will be made using the payment method already stored for your current subscription')}
            </Typography>
          }
          <Controller
            as={
              <PaymentMethods
                zuoraHostedPagesSource={config.zuoraSinglePageUpgradePaymentSource}
                showPaymentForm={showPaymentForm}
                selectedMethod={selectedPaymentMethod}
                onSelect={onPaymentMethodSelect}
                onResponse={onPaymentMethodCreated}
                onClientSideErrorHandler={onClientSidePaymentErrorHandler}
              />
            }
            name='showPaymentForm'
            control={control}
            defaultValue={false}
          />
        </Box>}
        {errors.showPaymentForm && (
          <Box sx={{ paddingLeft: spaces.md }}>
            <Typography variant='caption' color={palette.error.main} data-testid='payment-method-error'>
              {t(errors.showPaymentForm.message as string)}
            </Typography>
          </Box>
        )}
        {
          selectedPaymentMethod && isPaymentMethodPayPal(selectedPaymentMethod) &&
          payPalPaymentData?.type === 'braintree' &&
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: spaces.xxs,
              alignItems: 'center',
            }}
          >
            <CheckIcon sx={{ color: palette.text.primary }} fontSize='small'/>
            <Typography variant='body2' color={palette.text.secondary}>
              {t('Payment Method noted')}
            </Typography>
          </Box>
        }
      </Box>
      <Box
        data-testid='payment-form-licence-agreement'
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: spaces.md,
        }}>
        {renderLicenceAgreementText()}
      </Box>
      <Box
        data-testid='payment-form-captcha'
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        {renderCaptcha()}
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: spaces.sm,
        }}
      >
        <Button
          data-testid='payment-form-submit'
          type='submit'
          variant='contained'
          size='large'
          disabled={isButtonDisabled}
          aria-label={getButtonText()}
          aria-busy={isLoading}
          sx={{
            width: '100%',
            backgroundColor: palette.primary.main,
            color: palette.common.white,
            borderRadius: borderRadius.sm,
            padding: `${spaces.xs} ${spaces.lg}`,
          }}
        >
          {isLoading ? 
            <CircularProgress 
              size={26} 
              sx={{
                color: palette.common.white,
                '& .MuiCircularProgress-svg': {
                  height: 'auto',
                  width: 'auto',
                },
              }} 
              aria-label={t('Loading')} 
              aria-hidden='true' 
            /> 
            : getButtonText()}
        </Button>
        {isTrialLicence && (
          <Typography variant='caption' color={palette.text.secondary} textAlign='center'>
            {t('Chargeable after the trial period')}
          </Typography>
        )}  
      </Box>
      {isTrialLicence && (
        <Typography variant='caption' color={palette.text.secondary}>
          {t('After the 14 day test phase, you will be charged €<price> a month if you do not cancel ' +
            'your trial subscription. This is valid from <endDate>, for a duration of <months> months',
          licenceData)}
        </Typography>
      )}
      <SuccessModal 
        isOpen={isPaymentConfirmed || isError}
        showCloseButton={PaymentMode.PremiumRegistration === paymentMode || isError}
        showActionButton={PaymentMode.PremiumUpgrade === paymentMode && !isError}
        buttonLabel={PaymentMode.PremiumUpgrade === paymentMode ? t('Discover teaching materials') : undefined}
        onClose={onCloseModal}
        onSuccessModalConfirmButtonClick={onSuccessModalConfirmButtonClick}
        isLoading={isGettingUserInfo}
        {...getDialogInfo()}
      />
    </Box>
  );
};

export default PaymentForm;
